import { PublicClientApplication, NavigationClient } from "@azure/msal-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { ENV_OPTIONS } from "@constants";
import utils from "@utils";

/**
 * This class implements the INavigationClient interface to gain full control of
 * how navigation happens. In order to support *.netlify.app domains for preview
 * builds, we are using a custom proxy. This proxy helps bypass the lack of wildcard
 * of support in ADB2C.
 *
 * @see https://azuread.github.io/microsoft-authentication-library-for-js/ref/interfaces/_azure_msal_browser.inavigationclient.html
 * @see https://learn.microsoft.com/en-us/azure/active-directory/develop/reply-url#restrictions-on-wildcards-in-redirect-uris
 * @see https://github.com/RollKall/ad-proxy
 *
 * @class ProxyNavigationClient
 */
class ProxyNavigationClient {
	navigate = (url, options) => {
		if (options.noHistory) {
			window.location.replace(url);
		} else {
			window.location.assign(url);
		}

		return new Promise(resolve => {
			setTimeout(() => {
				resolve(true);
			}, options.timeout);
		});
	};

	navigateInternal(url, options) {
		return this.navigate(url, options);
	}

	navigateExternal(url, options) {
		const proxyUrl = new URL(ENV_OPTIONS.adb2c.proxyUrl);
		proxyUrl.searchParams.append("to", url);

		return this.navigate(proxyUrl.href, options);
	}
}

const navigationClient =
	ENV_OPTIONS.adb2c.proxyUrl !== undefined ? new ProxyNavigationClient() : new NavigationClient();

// Create PublicClientApplication instance
const msalInstance = new PublicClientApplication({
	auth: ENV_OPTIONS.adb2c.auth,
	cache: ENV_OPTIONS.adb2c.cache,
	system: { navigationClient },
});

/**
 * Get the list of claims attached to the signed-in user.
 *
 * @returns {Array<Object>}
 */
const getClaims = () =>
	msalInstance.getAccountByHomeId(localStorage.getItem("homeAccountId"))?.idTokenClaims;

/**
 * Check if the signed-in user is currently impersonating someone else.
 *
 * @returns {Boolean} `true` if the signed-in is impersonating someone else, `false` otherwise.
 */
const isImpersonating = () => {
	const claims = getClaims();

	return claims?.impersonatedUserEmail !== undefined;
};

const getAuthorityForPolicy = policyId => {
	const { policies } = ENV_OPTIONS.adb2c;

	return Object.values(policies).find(authority =>
		authority.toLowerCase().includes(policyId.toLowerCase()),
	);
};

// Acquire a token in order to interact with RollKall API.
//
const getToken = () => {
	// Return a dummy token if we are in test mode.
	const dummyToken = "TEST-TOKEN";
	if (localStorage.getItem("tm")) return Promise.resolve(dummyToken);

	const account = msalInstance.getAccountByHomeId(localStorage.getItem("homeAccountId"));

	if (account === null) {
		return Promise.resolve(dummyToken);
	}

	// We always need pick the authority based on the last policy that was used by the user.
	// If that is not the case, when `acquireTokenSilent` tries to get a new accessToken
	// using the refreshToken it will fail with an `invalid_grant` error (AADB2C90088).
	//
	// @see https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes
	const authority = getAuthorityForPolicy(account.idTokenClaims.acr);

	return msalInstance
		.acquireTokenSilent({
			account,
			authority,
			scopes: ENV_OPTIONS.adb2c.scopes,
		})
		.then(response => response.accessToken)
		.catch(error => {
			datadogLogs.logger.error(error.message);

			utils.dispatchUnauthorizedMessage();
		});
};

export default {
	msalInstance,
	getToken,
	isImpersonating,
	getClaims,
	getAuthorityForPolicy,
};
